import { inject, Injectable, signal } from "@angular/core";
import { ContactEngagement, Demographics, Person, PersonBasicInfo } from "@interfaces";
import { FuncService } from "../../../services";

export type ViewModePersonTab = 'overview' | 'engagements' | 'notes';
export type EditModePersonTab = 'basicInfo' | 'demographics' | 'emails' | 'webLinks' | 'permissions';

@Injectable({ providedIn: 'root' })
export class PersonDialogDataService {

	private func = inject(FuncService);
	public readonly viewModePersonTab = signal<ViewModePersonTab>('overview');
	public readonly editModePersonTab = signal<EditModePersonTab>('basicInfo');
	public readonly toEdit = signal<boolean>(false);
	public readonly personDetails = signal<Person | undefined>(undefined);
	public readonly emailErrorMsg = signal<string | undefined>(undefined);
	public readonly allEngagements = signal<ContactEngagement[]>([]);

	public async getPersonDetails(personId: number, toEdit: boolean) {
		const results = await this.func.person.getPersonDetails({ personId, toEdit });
		this.personDetails.set(results);
	}

	private updatePersonDetails(details: Partial<Person>) {
		this.personDetails.update((current) => { return current ? { ...current, ...details } : current });
	}

	public async updateBasicInfo(basicInfo: PersonBasicInfo) {
		const personDetails = this.personDetails();
		if (!personDetails) return;

		await this.func.person.updateBasicInfo({ personId: personDetails.personId, basicInfo });
		this.updatePersonDetails(basicInfo);
	}

	public async addEmail(email: string) {
		const personDetails = this.personDetails();
		if (!personDetails) return;

		const results = await this.func.email.addEmail({ personId: personDetails.personId, email });
		this.updatePersonDetails({ emails: [...results.emails] });
		if (results.errorMessage) {
			this.emailErrorMsg.set(results.errorMessage);
		}
	}

	public async setPrimaryEmail(emailId: number) {
		const personDetails = this.personDetails();
		if (!personDetails) return;

		const emails = await this.func.person.setPrimaryEmail({ personId: personDetails.personId, emailId });
		this.updatePersonDetails({ emails: [...emails] });
	}

	public async removeEmail(emailId: number) {
		const personDetails = this.personDetails();
		if (!personDetails) return;

		const emails = await this.func.email.removeEmail({ personId: personDetails.personId, emailId });
		this.updatePersonDetails({ emails: [...emails] });
	}

	public async updateDemographics(demographics: Demographics) {
		const personDetails = this.personDetails();
		if (!personDetails) return;

		await this.func.person.updateDemographics({ personId: personDetails.personId, demographics });
		this.updatePersonDetails({ demographics });
	}

	public async getAllEngagementsByPerson(personId: number) {
		const results = await this.func.engagement.getContactEngagementsByPerson({ personId });
		this.allEngagements.set(results);
	}

	public reloadData() {
		const personDetails = this.personDetails();
		if (!personDetails) return;

		this.getPersonDetails(personDetails.personId, this.toEdit());
		this.getAllEngagementsByPerson(personDetails.personId);
	}

}